/**
 * ProgramsView + ForkProgramModal — Australian National Campaigns catalogue.
 *
 * Extracted from App.js during the P1 Phase-2 refactor. Zero behavioural
 * changes: same props, same test-ids, same DOM.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import { API, MONTH_ORDER } from "../lib/beaconUtils";

export function ProgramsView({ onForked }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [mentalOnly, setMentalOnly] = useState(false);
  const [forking, setForking] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/programs`);
        if (!cancelled) { setData(r.data); setLoading(false); }
      } catch (e) {
        if (!cancelled) { setError(e.message); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <section className="beacon-library" data-testid="programs-view">
        <div className="beacon-tab-loading">
          <span className="beacon-spinner beacon-spinner-dark" />
          Loading the Australian programs catalogue…
        </div>
      </section>
    );
  }
  if (error) {
    return (
      <section className="beacon-library" data-testid="programs-view">
        <div className="beacon-tab-error">{error}</div>
      </section>
    );
  }
  if (!data) return null;

  const filtered = data.programs.filter((p) => {
    if (mentalOnly && !p.mental_health_related) return false;
    if (activeCategory !== "all" && p.category !== activeCategory) return false;
    return true;
  });

  return (
    <section className="beacon-library" data-testid="programs-view">
      <div className="beacon-sticky-head" data-testid="programs-sticky-head">
      <div className="beacon-plan-header">
        <div>
          <div className="beacon-card-eyebrow">PROGRAMS · NATIONAL CAMPAIGNS</div>
          <h2 className="beacon-card-title">
            Australian programs you can fork in.
          </h2>
          <p className="beacon-card-lede">
            A curated catalogue of national H&S programs — SafeWork Australia,
            international observances, NGO and charity campaigns, industry
            bodies. Pick the ones that fit your year and fork them onto the
            calendar. We never assume endorsement — you decide.
          </p>
        </div>
      </div>

      <div className="beacon-program-filters" data-testid="program-filters">
        <button
          type="button"
          data-testid="programs-cat-all"
          className={`beacon-chip${activeCategory === "all" ? " is-active" : ""}`}
          onClick={() => setActiveCategory("all")}
        >All ({data.total})</button>
        {Object.entries(data.categories).map(([key, label]) => {
          const count = data.programs.filter((p) => p.category === key).length;
          return (
            <button
              key={key}
              type="button"
              data-testid={`programs-cat-${key}`}
              className={`beacon-chip${activeCategory === key ? " is-active" : ""}`}
              onClick={() => setActiveCategory(key)}
            >{label} ({count})</button>
          );
        })}
        <button
          type="button"
          data-testid="programs-mental-toggle"
          className={`beacon-chip${mentalOnly ? " is-active" : ""}`}
          onClick={() => setMentalOnly((v) => !v)}
        >Mental health only</button>
      </div>
      </div>

      {filtered.length === 0 && (
        <div className="beacon-library-empty" data-testid="programs-empty">
          No programs match that filter.
        </div>
      )}

      <div className="beacon-library-grid">
        {filtered.map((p) => (
          <article
            key={p.id}
            className={`beacon-library-tile beacon-program-tile${
              p.mental_health_related ? " is-mental-health" : ""
            }`}
            data-testid={`program-tile-${p.id}`}
          >
            <div className="beacon-library-tile-kind">
              {p.category_label}
            </div>
            <div className="beacon-library-tile-title beacon-program-tile-title">
              {p.name}
            </div>
            <div className="beacon-program-tile-org">{p.owner_org}</div>
            <div className="beacon-program-tile-window">{p.run_window}</div>
            <div className="beacon-program-tile-body">{p.description}</div>
            {p.aligned_topic_ids.length > 0 && (
              <div className="beacon-library-tile-sources">
                {p.aligned_topic_ids.map((tid) => (
                  <code key={tid} data-testid={`program-aligned-${p.id}-${tid}`}>
                    {tid}
                  </code>
                ))}
              </div>
            )}
            <div className="beacon-library-tile-actions">
              <a
                className="beacon-tile-action"
                href={p.source_url}
                target="_blank"
                rel="noreferrer noopener"
                data-testid={`program-source-${p.id}`}
              >↗ source</a>
              <button
                type="button"
                className="beacon-tile-action"
                data-testid={`program-fork-${p.id}`}
                onClick={() => setForking(p)}
                disabled={p.aligned_topic_ids.length === 0}
                title={p.aligned_topic_ids.length === 0
                  ? "No aligned topic — pair this manually from the calendar."
                  : "Fork into Annual Plan"}
              >⑂ fork to plan</button>
            </div>
          </article>
        ))}
      </div>

      {forking && (
        <ForkProgramModal
          program={forking}
          onClose={() => setForking(null)}
          onDone={() => { setForking(null); onForked(); }}
        />
      )}
    </section>
  );
}

function ForkProgramModal({ program, onClose, onDone }) {
  const defaultMonth =
    program.month && MONTH_ORDER.includes(program.month)
      ? program.month : "January";
  const defaultWeek = program.week || 1;
  const [month, setMonth] = useState(defaultMonth);
  const [week, setWeek] = useState(defaultWeek);
  const [topicId, setTopicId] = useState(
    program.aligned_topic_ids[0] || ""
  );
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const slot = `${month.slice(0, 3).toLowerCase()}-w${week}`;

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/programs/${program.id}/fork-to-calendar`, {
        slot,
        override_topic_id: topicId || undefined,
        reason: reason.trim() || null,
      });
      onDone();
    } catch (e) {
      alert(`Fork failed: ${e.response?.data?.detail || e.message}`);
      setSaving(false);
    }
  };

  return (
    <div className="beacon-modal-backdrop" data-testid="fork-program-modal">
      <div className="beacon-modal">
        <div className="beacon-modal-head">
          <div>
            <div className="beacon-tiny-label">FORK PROGRAM TO CALENDAR</div>
            <div className="beacon-pack-title" data-testid="fork-program-name">
              {program.name}
            </div>
          </div>
          <button type="button" className="beacon-secondary-btn"
            data-testid="fork-program-close" onClick={onClose}>Close</button>
        </div>

        <div className="beacon-field">
          <label className="beacon-label">
            Aligned topic to drop into the slot
          </label>
          <select className="beacon-input"
            data-testid="fork-program-topic"
            value={topicId}
            onChange={(e) => setTopicId(e.target.value)}>
            {program.aligned_topic_ids.map((tid) => (
              <option key={tid} value={tid}>{tid}</option>
            ))}
          </select>
        </div>

        <div className="beacon-field">
          <label className="beacon-label">Month</label>
          <select className="beacon-input" value={month}
            data-testid="fork-program-month"
            onChange={(e) => setMonth(e.target.value)}>
            {MONTH_ORDER.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="beacon-field">
          <label className="beacon-label">Week</label>
          <select className="beacon-input" value={week}
            data-testid="fork-program-week"
            onChange={(e) => setWeek(Number(e.target.value))}>
            {[1, 2, 3, 4].map((w) => (
              <option key={w} value={w}>{`Week ${w}`}</option>
            ))}
          </select>
        </div>
        <div className="beacon-field">
          <label className="beacon-label">Reason (optional)</label>
          <input className="beacon-input" value={reason}
            data-testid="fork-program-reason"
            onChange={(e) => setReason(e.target.value)}
            placeholder={`e.g. anchor on ${program.name}`}
            maxLength={240} />
        </div>
        <div className="beacon-slot-editor-actions">
          <button type="button" className="beacon-cta beacon-cta-compact"
            data-testid="fork-program-confirm"
            disabled={saving} onClick={submit}>
            {saving ? "Forking…" : `Fork into ${slot}`}
          </button>
        </div>
      </div>
    </div>
  );
}
